/**
 * ClientFilter Component
 * Dropdown to filter analytics by specific client (optional)
 */

import { useClients } from '@/hooks/useClients';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Building2 } from 'lucide-react';

interface ClientFilterProps {
  value: string | undefined;
  onChange: (clientId: string | undefined) => void;
  className?: string;
}

export function ClientFilter({ value, onChange, className = '' }: ClientFilterProps) {
  const { data: clientsData, isLoading } = useClients({ status: 'active' });

  const clients = clientsData?.clients || [];

  const handleValueChange = (selectedValue: string) => {
    if (selectedValue === 'all') {
      onChange(undefined);
    } else {
      onChange(selectedValue);
    }
  };

  return (
    <div className={`flex flex-col gap-2 ${className}`}>
      <Label htmlFor="client-filter" className="flex items-center gap-2">
        <Building2 className="h-4 w-4" />
        Filter by Client
      </Label>
      <Select
        value={value || 'all'}
        onValueChange={handleValueChange}
        disabled={isLoading}
      >
        <SelectTrigger id="client-filter" className="w-[250px]">
          <SelectValue placeholder={isLoading ? 'Loading clients...' : 'Select a client'} />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All Clients</SelectItem>
          {clients.map((client) => (
            <SelectItem key={client.id} value={client.id}>
              <div className="flex items-center gap-2">
                <span>{client.name}</span>
                <span className="text-xs text-muted-foreground">
                  ({client.project_type})
                </span>
              </div>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {clients.length === 0 && !isLoading && (
        <p className="text-xs text-muted-foreground">No active clients found</p>
      )}
    </div>
  );
}
